<template>
  <v-card elevation="0" class="mt-4">
    <v-card-title>
      <v-icon left color="primary">mdi-table-clock</v-icon>
      <span style="font-size: 1.3rem; font-weight: 600">TD Classic + TD 2 Timesheet</span>
    </v-card-title>
    <v-card-subtitle>
      Time Doctor Classic (saved copy) plus live Time Doctor 2 hours, overlaps merged<span v-if="cachedFrom">
        (Classic cached {{ cachedFrom }} to {{ cachedTo }})</span>.
    </v-card-subtitle>
    <v-divider></v-divider>

    <v-card-text>
      <v-row dense align="center">
        <v-col cols="12" sm="3">
          <v-text-field v-model="from" type="date" label="From" outlined dense hide-details />
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field v-model="to" type="date" label="To" outlined dense hide-details />
        </v-col>
        <v-col cols="12" sm="auto">
          <v-progress-circular v-if="loading" indeterminate size="24" color="primary" />
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn outlined class="text-none" :disabled="!filteredEntries.length" @click="exportCsv">
            <v-icon left small>mdi-download</v-icon>
            Export CSV
          </v-btn>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn outlined class="text-none" :disabled="!summaryRows.length" @click="exportTdReport">
            <v-icon left small>mdi-file-pdf-box</v-icon>
            Export PDF (TD format)
          </v-btn>
        </v-col>
      </v-row>

      <v-row dense class="mt-2">
        <v-col cols="12" sm="6">
          <v-autocomplete
            v-model="selectedUserIds"
            :items="users"
            :item-text="userLabel"
            item-value="user_id"
            label="Search users (up to 25)"
            :loading="loadingUsers"
            :search-input.sync="userSearch"
            @change="userSearch = null"
            multiple
            chips
            small-chips
            deletable-chips
            clearable
            outlined
            dense
            hide-details="auto"
            :disabled="!users.length"
            :rules="[(v) => v.length <= 25 || 'Up to 25 users at a time']"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-switch v-model="hideZero" label="Hide users with no tracked time" dense hide-details class="mt-1" />
        </v-col>
      </v-row>

      <v-alert v-if="error" type="error" dense text class="mt-4">{{ error }}</v-alert>
      <v-alert v-for="w in warnings" :key="w" type="warning" dense text class="mt-4">{{ w }}</v-alert>

      <div v-if="!selectedUserIds.length" class="mt-4 grey--text">Search for one or more users to load their hours.</div>

      <template v-if="loaded && selectedUserIds.length">
        <div class="mt-4 text-subtitle-2">
          Total: {{ formatSeconds(grandTotal) }} across {{ summaryRows.length }} user(s)
        </div>

        <v-data-table
          :headers="summaryHeaders"
          :items="summaryRows"
          item-key="user_id"
          show-expand
          single-expand
          dense
          :items-per-page="50"
          sort-by="total"
          sort-desc
          class="mt-2"
        >
          <template v-for="d in days" v-slot:[`item.${d}`]="{ item }">
            <span :key="d" :class="item.days[d] ? '' : 'grey--text text--lighten-1'">
              {{ item.days[d] ? formatSeconds(item.days[d]) : '-' }}
            </span>
          </template>
          <template v-slot:[`item.total`]="{ item }">
            <strong>{{ formatSeconds(item.total) }}</strong>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-2">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Start</th>
                    <th>End</th>
                    <th>Duration</th>
                    <th>Project</th>
                    <th>Task</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="e in entriesForUser(item.user_id)" :key="e.id || e.start_time">
                    <td>{{ e.start_time }}</td>
                    <td>{{ e.end_time }}</td>
                    <td>{{ formatSeconds(e.length_seconds) }}</td>
                    <td>{{ e.project_name }}</td>
                    <td>{{ e.task_name }}</td>
                    <td>{{ e.source === 'td2' ? 'TD2' : 'Classic' }}</td>
                  </tr>
                  <tr v-if="!entriesForUser(item.user_id).length">
                    <td colspan="6" class="grey--text">No entries</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </td>
          </template>
        </v-data-table>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "ClassicTimesheetView",
  data() {
    return {
      from: "2026-10-01",
      to: "2026-11-02",
      cachedFrom: null,
      cachedTo: null,
      loading: false,
      loaded: false,
      error: null,
      warnings: [],
      users: [],
      loadingUsers: false,
      userSearch: null,
      loadSeq: 0,
      entries: [],
      selectedUserIds: [],
      hideZero: true,
    };
  },
  computed: {
    days() {
      const days = [];
      let d = this.$date(this.from);
      const end = this.$date(this.to);
      while (d.isValid() && !d.isAfter(end, "day") && days.length < 62) {
        days.push(d.format("YYYY-MM-DD"));
        d = d.add(1, "day");
      }
      return days;
    },
    filteredEntries() {
      if (!this.selectedUserIds.length) return [];
      const ids = this.selectedUserIds.map(String);
      return this.entries.filter((e) => ids.includes(String(e.user_id)));
    },
    summaryRows() {
      const byUser = {};
      const visibleUsers = this.selectedUserIds.length
        ? this.users.filter((u) => this.selectedUserIds.map(String).includes(String(u.user_id)))
        : this.users;
      visibleUsers.forEach((u) => {
        byUser[u.user_id] = { user_id: u.user_id, name: this.userLabel(u), days: {}, total: 0 };
      });
      this.filteredEntries.forEach((e) => {
        if (!byUser[e.user_id]) {
          byUser[e.user_id] = { user_id: e.user_id, name: e.user_name || `User ${e.user_id}`, days: {}, total: 0 };
        }
        const row = byUser[e.user_id];
        row.days[e.date] = (row.days[e.date] || 0) + e.length_seconds;
        row.total += e.length_seconds;
      });
      let rows = Object.values(byUser);
      if (this.hideZero) rows = rows.filter((r) => r.total > 0);
      return rows;
    },
    summaryHeaders() {
      return [
        { text: "User", value: "name" },
        ...this.days.map((d) => ({ text: this.$date(d).format("ddd D MMM"), value: d, sortable: false })),
        { text: "Total", value: "total" },
        { text: "", value: "data-table-expand" },
      ];
    },
    grandTotal() {
      return this.summaryRows.reduce((sum, r) => sum + r.total, 0);
    },
  },
  watch: {
    from: "load",
    to: "load",
    selectedUserIds: "load",
  },
  mounted() {
    this.loadUsers();
  },
  methods: {
    userLabel(u) {
      if (!u.full_name) return u.email || `User ${u.user_id}`;
      return u.email ? `${u.full_name} (${u.email})` : u.full_name;
    },
    formatSeconds(s) {
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      return `${h}h ${String(m).padStart(2, "0")}m`;
    },
    entriesForUser(userId) {
      return this.filteredEntries
        .filter((e) => String(e.user_id) === String(userId))
        .sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)));
    },
    // People to pick from (no hours); hours load only for the selected users
    async loadUsers() {
      this.loadingUsers = true;
      try {
        const response = await this.$axios.get("/api/payroll/time-doctor/timesheet-users");
        if (response.data?.response) {
          this.users = response.data.data.users || [];
          this.cachedFrom = response.data.data.cached_from;
          this.cachedTo = response.data.data.cached_to;
        } else {
          this.error = response.data?.message || "Failed to load Time Doctor users";
        }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to load Time Doctor users";
      } finally {
        this.loadingUsers = false;
      }
    },
    async load() {
      this.error = null;
      this.warnings = [];
      if (!this.from || !this.to || this.from > this.to) return;
      if (!this.selectedUserIds.length || this.selectedUserIds.length > 25) {
        this.entries = [];
        this.loaded = false;
        return;
      }
      const seq = ++this.loadSeq;
      this.loading = true;
      try {
        const response = await this.$axios.get("/api/payroll/time-doctor/classic-timesheet", {
          params: { from: this.from, to: this.to, user_ids: this.selectedUserIds },
        });
        if (seq !== this.loadSeq) return;
        if (response.data?.response) {
          this.entries = response.data.data.entries || [];
          this.warnings = response.data.data.warnings || [];
          this.loaded = true;
        } else {
          this.error = response.data?.message || "Failed to load Time Doctor timesheet";
        }
      } catch (error) {
        if (seq === this.loadSeq) this.error = error.response?.data?.message || "Failed to load Time Doctor timesheet";
      } finally {
        if (seq === this.loadSeq) this.loading = false;
      }
    },
    // Time Doctor report style: "8h 2m", "43m", "0h 0m"
    formatTd(s) {
      const h = Math.floor((s || 0) / 3600);
      const m = Math.floor(((s || 0) % 3600) / 60);
      if (!h && m) return `${m}m`;
      return `${h}h ${m}m`;
    },
    tdDayLabel(d) {
      const day = this.$date(d);
      const n = day.date();
      const sfx = n % 100 >= 11 && n % 100 <= 13 ? "th" : { 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th";
      return `${day.format("MMM")} ${n}${sfx},<br>${day.format("YYYY")}`;
    },
    // Opens a print-ready page matching the TD Classic report layout; save as PDF from the print dialog
    exportTdReport() {
      const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
      const nameOf = (r) => {
        const u = this.users.find((x) => String(x.user_id) === String(r.user_id));
        return (u && u.full_name) || r.name;
      };
      const rows = [...this.summaryRows].sort((a, b) => String(nameOf(a)).localeCompare(String(nameOf(b))));
      let html = "";
      for (let i = 0; i < this.days.length; i += 7) {
        const chunk = this.days.slice(i, i + 7);
        const first = i === 0;
        html += `<table><thead><tr><th class="name">${first ? "Name" : ""}</th><th class="total">${first ? "Total Time" : ""}</th>`;
        html += chunk.map((d) => `<th>${this.tdDayLabel(d)}</th>`).join("");
        html += `</tr></thead><tbody>`;
        rows.forEach((r) => {
          html += `<tr><td>${esc(nameOf(r))}</td><td>${first ? this.formatTd(r.total) : ""}</td>`;
          html += chunk.map((d) => `<td>${this.formatTd(r.days[d])}</td>`).join("");
          html += `</tr>`;
        });
        html += `</tbody></table>`;
      }
      const logo = `<svg width="44" height="44" viewBox="0 0 44 44"><circle cx="22" cy="23" r="17" fill="none" stroke="#222" stroke-width="4"/><path d="M14 23l7 7 17-19" fill="none" stroke="#222" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      const doc = `<!DOCTYPE html><html><head><title>TimeDoctor Report ${this.from} to ${this.to}</title><style>
        @page { size: A4 landscape; margin: 15mm; }
        body { font-family: "Times New Roman", Times, serif; color: #000; margin: 20px; }
        .header { display: flex; align-items: center; gap: 12px; margin-bottom: 30px; font-family: Arial, Helvetica, sans-serif; }
        .brand { font-size: 22px; color: #333; } .brand b { color: #111; } .brand small { font-size: 14px; letter-spacing: 1px; }
        table { border-collapse: collapse; table-layout: fixed; page-break-inside: avoid; }
        th, td { text-align: left; padding: 8px; width: 90px; font-size: 15px; vertical-align: bottom; }
        th.name, td:first-child { width: 160px; } th.total, td:nth-child(2) { width: 120px; }
        th { padding-top: 14px; border-bottom: 1px solid #000; }
        td { border-bottom: 1px solid #000; }
      </style></head><body>
        <div class="header">${logo}<div class="brand">Time<b>Doctor</b> <small>- REPORTS</small></div></div>
        ${html}
        <script>window.onload = function () { window.print(); };</scr` + `ipt>
      </body></html>`;
      const w = window.open("", "_blank");
      if (!w) {
        this.$toast.error("Allow pop-ups to export the PDF");
        return;
      }
      w.document.write(doc);
      w.document.close();
    },
    exportCsv() {
      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const userNames = {};
      this.users.forEach((u) => (userNames[u.user_id] = u));
      const lines = [["User", "Email", "Date", "Start", "End", "Duration (h)", "Project", "Task", "Source"].join(",")];
      this.filteredEntries.forEach((e) => {
        const u = userNames[e.user_id] || {};
        lines.push(
          [
            u.full_name || e.user_name,
            u.email,
            e.date,
            e.start_time,
            e.end_time,
            (e.length_seconds / 3600).toFixed(2),
            e.project_name,
            e.task_name,
            e.source === "td2" ? "TD2" : "Classic",
          ]
            .map(esc)
            .join(",")
        );
      });
      const blob = new Blob([lines.join("\n")], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `td-timesheet_${this.from}_${this.to}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    },
  },
};
</script>
